import { useMemo } from "react";
import type { AccountDrilldownDetail } from "../../lib/api-modules/types";
import { useQuery } from "@tanstack/react-query";
import { listFamilyMembers } from "../../lib/api-modules/family-members";
import {
  isApproachingMilestone,
  utmaAgeOfMajority,
  utmaMilestones,
  type UtmaMilestone,
} from "../../lib/util/utma";

/**
 * UTMA milestone panel — Phase 5 Session 5. Display-only.
 *
 * Renders a timeline of milestone calendar dates derived from the
 * beneficiary's date_of_birth + the account's `plan_state_code` (governing
 * UTMA statute → age of majority). Phase 13 will wire alert generation
 * off the same `utmaMilestones` helper; v1 only surfaces the dates.
 *
 * Banner conditions:
 *   - Approaching milestone within 12 months → soft amber banner
 *   - All milestones passed → "fully turned over" banner
 *   - DOB missing → encouragement to add it
 */
export function UtmaMilestonePanel({
  account,
}: {
  readonly account: AccountDrilldownDetail;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["family-members", { includeArchived: true }],
    queryFn: () => listFamilyMembers(true),
  });
  const beneficiary = useMemo(
    () =>
      account.beneficiary_member_id
        ? data?.family_members?.find(
            (m) => m.member_id === account.beneficiary_member_id,
          )
        : undefined,
    [data, account.beneficiary_member_id],
  );
  const stateCode = account.plan_state_code ?? "NY";
  const ageOfMajority = utmaAgeOfMajority(stateCode);
  const milestones = useMemo<UtmaMilestone[]>(
    () => (beneficiary ? utmaMilestones(beneficiary, stateCode) : []),
    [beneficiary, stateCode],
  );
  const approaching = useMemo(
    () =>
      beneficiary ? isApproachingMilestone(beneficiary, stateCode) : false,
    [beneficiary, stateCode],
  );

  if (isLoading) {
    return null;
  }

  return (
    <section className="mt-6 rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold">UTMA milestones</h3>
        <p className="text-xs text-zinc-500">
          Governing state: <strong>{stateCode}</strong> · Age of majority:{" "}
          <strong>{ageOfMajority}</strong>
        </p>
      </header>
      {!account.beneficiary_member_id && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          Set a beneficiary on the account to see milestone dates.
        </p>
      )}
      {account.beneficiary_member_id && !beneficiary && (
        <p className="text-sm text-zinc-500">Loading beneficiary…</p>
      )}
      {beneficiary && !beneficiary.date_of_birth && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          Add <strong>{beneficiary.name}</strong>'s date of birth on the family
          page to see milestone dates.
        </p>
      )}
      {milestones.length > 0 && (
        <>
          {approaching && (
            <p className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              A milestone is within 12 months — review the upcoming entry below.
            </p>
          )}
          <ol className="space-y-2">
            {milestones.map((m) => (
              <li
                key={`${m.age}-${m.date}`}
                className={`grid grid-cols-1 gap-1 rounded-md border p-3 sm:grid-cols-[6rem_8rem_1fr] sm:items-center ${
                  m.is_age_of_majority
                    ? "border-emerald-400 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/30"
                    : "border-zinc-200 dark:border-zinc-800"
                }`}
              >
                <div className="text-sm font-medium">Age {m.age}</div>
                <div className="text-sm tabular-nums text-zinc-600 dark:text-zinc-300">
                  {m.date}
                </div>
                <div className="text-sm">{m.action}</div>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-zinc-500">
            States have varying UTMA-statute defaults; this panel uses the
            stock age-of-majority for {stateCode}. If the donor specified a
            non-default age in the trust document (CA / NV / TN allow
            18-25), treat the timeline as informational only.
          </p>
        </>
      )}
    </section>
  );
}
